import { gql } from '@apollo/client';
import {
    PROJECT_LESS_FIELDS_FRAGMENT,
    PROJECT_WITH_TASKS_FRAGMENT,
    PROJECT_USERS_FRAGMENT,
    PROJECT_FIELDS_FRAGMENT,
    SIMPLE_PROJECT_FIELDS_FRAGMENT,
    PLANNING_PROJECT_LESS_FIELDS_FRAGMENT
} from './fragments/project';
import { TASK_WITH_RESOURCED_FIELDS_FRAGMENT } from './fragments/task';
import { MACHINE_FIELDS_FRAGMENT } from './fragments/machine';
import { PROJECT_ORGANIZATION_FIELDS_FRAGMENT } from './fragments/organizations';
import { PROJECTS_CONTAINER_PROJECTS_FRAGMENT } from '../../containers/ProjectsContainer/api/fragments/fragments';
import { FR_PORTFOLIO_LOCATION_FIELDS } from './fragments/location';

export const GET_PROJECT_ROWS = gql`
    query projectRows($ProjectIds: [Int!], $Search: String, $From: Int, $To: Int) {
        projectRows(data: { ProjectIds: $ProjectIds, Search: $Search, From: $From, To: $To }) {
            TotalRowCount
            Project {
                ProjectId
                Name
                ProjectNbr
            }
        }
    }
`;

export const GET_PROJECTS = gql`
    query projects {
        projects {
            ...ProjectWithTasks
        }
    }
    ${PROJECT_WITH_TASKS_FRAGMENT}
`;

export const GET_MACHINE_PROJECTS = gql`
    query machineById($MachineId: Int!) {
        machineById(data: { MachineId: $MachineId }) {
            Projects {
                ...ProjectLessFields
            }
        }
    }
    ${PROJECT_LESS_FIELDS_FRAGMENT}
`;

export const GET_PROJECT_USERS = gql`
    query projectUsersById($ProjectId: Int!) {
        projectById(data: { ProjectId: $ProjectId }) {
            ...ProjectUsers
        }
    }
    ${PROJECT_USERS_FRAGMENT}
`;

export const GET_PROJECT_MACHINES = gql`
    query projectMachinesById($ProjectId: Int!) {
        projectById(data: { ProjectId: $ProjectId }) {
            ProjectId
            Machines {
                ...MachineFields
            }
        }
    }
    ${MACHINE_FIELDS_FRAGMENT}
`;

export const GET_PROJECT_USERS_AND_MACHINES = gql`
    query projectUsersAndMachinesById($ProjectId: Int!) {
        projectById(data: { ProjectId: $ProjectId }) {
            ...ProjectUsers
            Machines {
                ...MachineFields
            }
        }
    }
    ${PROJECT_USERS_FRAGMENT}
    ${MACHINE_FIELDS_FRAGMENT}
`;

export const GET_ORGANIZATION_PROJECT = gql`
    query projectsByOrganizationId($OrganizationId: Int!) {
        projectsByOrganizationId(data: { OrganizationId: $OrganizationId }) {
            ...ProjectFields
        }
    }
    ${PROJECT_FIELDS_FRAGMENT}
`;

export const GET_ALL_PROJECTS_TASKS = gql`
    query getAllProjectsAndTasks {
        projects {
            ProjectId
            Tasks {
                ...TaskResourcedFields
            }
        }
    }
    ${TASK_WITH_RESOURCED_FIELDS_FRAGMENT}
`;

export const GET_PROJECT_TASK = gql`
    query projectTasksById($ProjectId: Int!) {
        projectById(data: { ProjectId: $ProjectId }) {
            ProjectId
            Tasks {
                ...TaskResourcedFields
            }
        }
    }
    ${TASK_WITH_RESOURCED_FIELDS_FRAGMENT}
`;

export const GET_PLANNING_PROJECT_TASKS = gql`
    query getPlanningProjectTasks($ProjectId: Int!) {
        projectById(data: { ProjectId: $ProjectId }) {
            ProjectId
            Tasks(data: { TaskStatus: [ASSIGNED, RESOURCED, ONGOING, CANCELLED, FAILED, PAUSED] }) {
                TaskId
                TaskType {
                    TaskTypeId
                    Name
                    Abbr
                    TaskGroup
                }
                TaskStatus {
                    TaskStatusId
                    Name
                }
                Location {
                    Name
                }
            }
        }
    }
`;

export const PLANNING_ADD_TASK_TO_PROJECT = gql`
    mutation addTaskToProject(
        $WorkPackageId: Int!
        $TaskIds: [Int!]
        $UserId: Int!
        $FromProjectId: Int
    ) {
        addTaskToProject(
            data: {
                WorkPackageId: $WorkPackageId
                TaskIds: $TaskIds
                UserId: $UserId
                FromProjectId: $FromProjectId
            }
        ) {
            ...PlanningProjectLessFields
        }
    }
    ${PLANNING_PROJECT_LESS_FIELDS_FRAGMENT}
`;

export const GET_PROJECT_TASKS_NOT_RESOURCED = gql`
    query getProjectTasksNotResourced {
        projects {
            ...SimpleProjectFields
            NOT_RESOURCED: Tasks(
                data: { TaskResourceStatus: NOT_RESOURCED, TaskStatus: [ASSIGNED] }
            ) {
                ...TaskResourcedFields
            }
            RESOURCED: Tasks(
                data: { TaskResourceStatus: RESOURCED, TaskStatus: [RESOURCED, ONGOING] }
            ) {
                ...TaskResourcedFields
            }
            Tasks(data: { TaskResourceStatus: NOT_RESOURCED }) {
                ...TaskResourcedFields
            }
        }
    }
    ${SIMPLE_PROJECT_FIELDS_FRAGMENT}
    ${TASK_WITH_RESOURCED_FIELDS_FRAGMENT}
`;

export const GET_PROJECT_ORGANIZATIONS = gql`
    query projectOrganizationsById($ProjectId: Int!) {
        projectById(data: { ProjectId: $ProjectId }) {
            ProjectId
            ProjectOrganization {
                ...ProjectOrganizationFields
            }
        }
    }
    ${PROJECT_ORGANIZATION_FIELDS_FRAGMENT}
`;

export const GET_PROJECTSTATUS = gql`
    query ProjectStatus {
        projectStatuses {
            ProjectStatusId
            Name
        }
    }
`;

export const CREATE_PROJECT = gql`
    mutation createProject(
        $ProjectName: String!
        $ProjectNumber: String!
        $StartDate: Date
        $EndDate: Date
        $Target: String
        $WorkPackageId: Int
        $WorkName: String
        $WorkNumber: String
        $Cables: Boolean
        $Pipes: Boolean
        $Lines: Boolean
        $LocationMoveAllowedAtObstacles: Boolean
        $MaxLocationMove: Float
        $AgreeLocationMoveWithDesigner: Boolean
        $EpicId: Int
        $EpicName: String
        $EpicNumber: String
        $CoordinateSystemTypeId: Int
        $HeightSystemTypeId: Int
        $Locations: [CreateLocationInputType!]
        $DataOwner: String
        $Secret: Boolean
        $PossibleStartDate: Date
        $CableShowingDate: Date
        $Addr: String
        $AddResourceAssets: [AddResourceAssetProjectInputType!]
        $OrganizationAccess: Boolean
    ) {
        createProject(
            data: {
                ProjectName: $ProjectName
                ProjectNumber: $ProjectNumber
                WorkPackageId: $WorkPackageId
                WorkName: $WorkName
                WorkNumber: $WorkNumber
                Cables: $Cables
                Pipes: $Pipes
                Lines: $Lines
                LocationMoveAllowedAtObstacles: $LocationMoveAllowedAtObstacles
                MaxLocationMove: $MaxLocationMove
                AgreeLocationMoveWithDesigner: $AgreeLocationMoveWithDesigner
                EpicId: $EpicId
                EpicName: $EpicName
                EpicNumber: $EpicNumber
                StartDate: $StartDate
                EndDate: $EndDate
                Target: $Target
                CoordinateSystemTypeId: $CoordinateSystemTypeId
                HeightSystemTypeId: $HeightSystemTypeId
                Locations: $Locations
                EpicDataOwner: $DataOwner
                EpicSecret: $Secret
                PossibleStartDate: $PossibleStartDate
                CableShowingDate: $CableShowingDate
                Addr: $Addr
                AddResourceAssets: $AddResourceAssets
                OrganizationAccess: $OrganizationAccess
            }
        ) {
            ...ProjectFields
        }
    }
    ${PROJECT_FIELDS_FRAGMENT}
`;

export const UPDATE_PROJECT = gql`
    mutation updateProject(
        $ProjectId: Int!
        $ProjectName: String
        $ProjectNumber: String
        $StartDate: Date
        $EndDate: Date
        $Target: String
        $WorkName: String
        $WorkNumber: String
        $Cables: Boolean
        $Pipes: Boolean
        $Lines: Boolean
        $LocationMoveAllowedAtObstacles: Boolean
        $MaxLocationMove: Float
        $AgreeLocationMoveWithDesigner: Boolean
        $EpicName: String
        $EpicNumber: String
        $CoordinateSystemTypeId: Int
        $HeightSystemTypeId: Int
        $ProjectStatusId: Int
        $AddProjectUsers: [UpdateProjectAddUsersInputType!]
        $RemoveProjectUsers: [UpdateProjectRemoveUsersInputType!]
        $AddTaskTypeInstruction: [UpdateProjectAddTaskTypeInstruction!]
        $RemoveTaskTypeInstruction: [UpdateProjectRemoveTaskTypeInstruction!]
        $DataSource: WorkPackageDataSourceInputType
        $AddInspection: [UpdateProjectAddInspection!]
        $RemoveInspection: [UpdateProjectRemoveInspection!]
        $PossibleStartDate: Date
        $CableShowingDate: Date
        $Addr: String
        # $AddMachines: [ProjectAddRemoveMachinesInputType!]
        # $RemoveMachines: [ProjectAddRemoveMachinesInputType!]
        $AddResourceAssets: [AddResourceAssetProjectInputType!]
        $RemoveResourceAssets: [RemoveResourceAssetProjectInputType!]
    ) {
        updateProject(
            data: {
                ProjectId: $ProjectId
                ProjectName: $ProjectName
                ProjectNumber: $ProjectNumber
                WorkName: $WorkName
                WorkNumber: $WorkNumber
                Cables: $Cables
                Pipes: $Pipes
                Lines: $Lines
                LocationMoveAllowedAtObstacles: $LocationMoveAllowedAtObstacles
                MaxLocationMove: $MaxLocationMove
                AgreeLocationMoveWithDesigner: $AgreeLocationMoveWithDesigner
                EpicName: $EpicName
                EpicNumber: $EpicNumber
                StartDate: $StartDate
                EndDate: $EndDate
                Target: $Target
                CoordinateSystemTypeId: $CoordinateSystemTypeId
                HeightSystemTypeId: $HeightSystemTypeId
                ProjectStatusId: $ProjectStatusId
                AddProjectUsers: $AddProjectUsers
                RemoveProjectUsers: $RemoveProjectUsers
                AddTaskTypeInstruction: $AddTaskTypeInstruction
                RemoveTaskTypeInstruction: $RemoveTaskTypeInstruction
                DataSource: $DataSource
                AddInspection: $AddInspection
                RemoveInspection: $RemoveInspection
                PossibleStartDate: $PossibleStartDate
                CableShowingDate: $CableShowingDate
                Addr: $Addr
                # AddMachines: $AddMachines
                # RemoveMachines: $RemoveMachines
                AddResourceAssets: $AddResourceAssets
                RemoveResourceAssets: $RemoveResourceAssets
            }
        ) {
            ...ProjectFields
        }
    }
    ${PROJECT_FIELDS_FRAGMENT}
`;

export const UPDATE_PROJECT_STATUS = gql`
    mutation updateProjectStatus($ProjectId: Int!, $ProjectStatusId: Int) {
        updateProject(data: { ProjectId: $ProjectId, ProjectStatusId: $ProjectStatusId }) {
            ...ProjectsContainerProjects
        }
    }
    ${PROJECTS_CONTAINER_PROJECTS_FRAGMENT}
`;

export const CREATE_LOCATIONS_TO_PROJECT = gql`
    mutation createLocationsToProject(
        $ProjectId: Int!
        $Locations: [CreateLocationInputType!]
        $ResourceToAll: Boolean
    ) {
        createLocationsToProject(
            data: { ProjectId: $ProjectId, Locations: $Locations, ResourceToAll: $ResourceToAll }
        )
    }
`;

export const CREATE_PROJECT_LOCATIONS = gql`
    mutation createProjectLocations(
        $ProjectId: Int!
        $Locations: [CreateLocationInputType!]
        $ResourceToAll: Boolean
    ) {
        createProjectLocations(
            data: {
                ProjectId: $ProjectId
                Locations: $Locations
                ResourceToAll: $ResourceToAll
            }
        ) {
            ...fr_PortfolioLocationFields
        }
    }
    ${FR_PORTFOLIO_LOCATION_FIELDS}
`;

export const DELETE_PROJECT = gql`
    mutation deleteProject($ProjectId: Int!) {
        deleteProject(data: { ProjectId: $ProjectId })
    }
`;

export const GET_USER_PROJECTROLES = gql`
    query userProjectRoleTypes {
        userProjectRoleTypes {
            UserProjectRoleTypeId
            Name
        }
    }
`;

export const ADD_USER_PROJECTROLE = gql`
    mutation addUserProjectRole($ProjectId: Int!, $UserId: Int!, $UserProjectRoleTypeId: Int!) {
        addUserProjectRole(
            data: {
                ProjectId: $ProjectId
                UserId: $UserId
                UserProjectRoleTypeId: $UserProjectRoleTypeId
            }
        ) {
            ...ProjectsContainerProjects
        }
    }
    ${PROJECTS_CONTAINER_PROJECTS_FRAGMENT}
`;

export const REMOVE_USER_PROJECTROLE = gql`
    mutation removeUserProjectRole($ProjectId: Int!, $UserId: Int!, $UserProjectRoleTypeId: Int!) {
        removeUserProjectRole(
            data: {
                ProjectId: $ProjectId
                UserId: $UserId
                UserProjectRoleTypeId: $UserProjectRoleTypeId
            }
        ) {
            ...ProjectsContainerProjects
        }
    }
    ${PROJECTS_CONTAINER_PROJECTS_FRAGMENT}
`;

export const ADD_USER_PROJECTROLE_TO_ORGANIZATION = gql`
    mutation addUserProjectRoleOwnOrganization($ProjectId: Int!) {
        addUserProjectRoleOwnOrganization(data: { ProjectId: $ProjectId }) {
            ...ProjectsContainerProjects
        }
    }
    ${PROJECTS_CONTAINER_PROJECTS_FRAGMENT}
`;

export const UPLOAD_TASK_REPORT = gql`
    mutation uploadTaskReport($File: Upload!, $ProjectId: Int!) {
        uploadTaskReport(data: { File: $File, ProjectId: $ProjectId })
    }
`;

export const GET_TASK_REPORT = gql`
    query taskRaport($TaskId: Int!) {
        taskRaport(data: { TaskId: $TaskId }) {
            TaskRaportId
            InUse
            Header
            DataTypes
            DataRowCount
            Task {
                ...TaskResourcedFields
            }
        }
    }
    ${TASK_WITH_RESOURCED_FIELDS_FRAGMENT}
`;

export const GET_LOCATION_REPORTS = gql`
    query taskLocationRaport($LocationId: Int!) {
        taskRaport(data: { LocationId: $LocationId }) {
            TaskRaportId
            InUse
            Header
            DataTypes
            RaportType
            DataRowCount
            SGFRowObject {
                Key
                Value
                Name
            }
            Task {
                ...TaskResourcedFields
            }
        }
    }
    ${TASK_WITH_RESOURCED_FIELDS_FRAGMENT}
`;

export const GET_REPORT_FILE = gql`
    query teklaFile($TaskRaportId: Int!) {
        teklaFile(data: { TaskRaportId: $TaskRaportId })
    }
`;

export const GET_TASKRAPORT_FILE = gql`
    query taskRaportFile(
        $EpicId: Int
        $ProjectId: Int
        $LocationId: Int
        $WorkPackageId: Int
        $TaskId: Int
    ) {
        taskRaportFile(
            data: {
                EpicId: $EpicId
                ProjectId: $ProjectId
                LocationId: $LocationId
                WorkPackageId: $WorkPackageId
                TaskId: $TaskId
            }
        )
    }
`;

export const PUBLISH_REPORT_FILE = gql`
    mutation publishTaskRaport($LocationId: Int, $ProjectId: Int, $TaskRaportId: Int) {
        publishTaskRaport(
            data: { ProjectId: $ProjectId, LocationId: $LocationId, TaskRaportId: $TaskRaportId }
        )
    }
`;

export const GET_PROJECT_REPORT_FILE = gql`
    query projectTekla($ProjectId: Int!) {
        projectTekla(data: { ProjectId: $ProjectId })
    }
`;

export const GET_REPORT_DATA =
    gql(`query taskRaportData($TaskRaportId: Int!, $From: Int, $To: Int) { taskRaportData(data: {TaskRaportId: $TaskRaportId, From: $From, To: $To}) {
	TaskRaportRowId
	RowData
	RowType
}}`);

export const GET_COMBINED_PROJECT_REPORT = gql(`
query combinedProjectRaport($ProjectId: Int!) { combinedProjectRaport(data: {ProjectId: $ProjectId}) {
	FileName
	DataBase64
}}`);

export const GET_COMBINED_LOCATION_REPORT = gql(`
query CombinedLocationRaport($LocationId: Int!) { CombinedLocationRaport(data: {LocationId: $LocationId}) {
	FileName
	DataBase64
}}`);
